/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { get as getUser } from 'js/lib/user';

import { Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachButton } from 'bundles/ai-coach-platform/components/building-blocks/CoachButton';
import { firstNameFromFullName } from 'bundles/ai-coach-platform/utils/nameParser';
import { getActiveCoachModeId } from 'bundles/wes-common/components/WESAskCoursera/constants';

import _t from 'i18n!nls/wes-common';

export type QuickPrompt = {
  key: string;
  prompt: string;
};

type WESAskCourseraEmptyStateProps = {
  sendMessage: (message: string) => void;
  quickPrompts?: QuickPrompt[];
  welcomeMessage?: string;
};

const styles = {
  root: css`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--cds-spacing-400) var(--cds-spacing-200);
  `,
  icon: css`
    margin: var(--cds-spacing-300) 0;
    text-align: center;
    width: 100%;
  `,
  title: css`
    color: var(--cds-color-emphasis-primary-background-default);
  `,
  subtitle: css`
    margin: 0 0 var(--cds-spacing-400) 0;
  `,
  promptsTitle: css`
    margin: var(--cds-spacing-400) 0 var(--cds-spacing-200) 0;
  `,
  promptsContainer: css`
    margin-top: var(--cds-spacing-200);
  `,
  promptButton: css`
    margin-bottom: var(--cds-spacing-100);
  `,
};

const WESAskCourseraEmptyState = ({
  sendMessage,
  quickPrompts = [],
  welcomeMessage,
}: WESAskCourseraEmptyStateProps) => {
  const { fullName: userName } = getUser();
  const userFirstName = userName ? firstNameFromFullName(userName) : null;
  const displayTitle = userFirstName ? _t('Hi #{userFirstName}!', { userFirstName }) : _t('Hi there!');
  const displaySubtitle = welcomeMessage || _t('How can I help you?');
  const track = useTracker();
  const activeCoachModeId = getActiveCoachModeId();
  const handlePromptClick = (prompt: string) => {
    sendMessage(prompt);
    track('interact_coach', {
      coachInteractionType: 'click_action',
      coach: {
        mode: activeCoachModeId,
        location: 'chat',
      },
      coachAction: prompt,
    });
  };

  return (
    <div className="ai-coach-chat-empty-state" css={styles.root}>
      <div>
        <Typography2 component="h3" variant="titleSmall" css={styles.title}>
          {displayTitle}
        </Typography2>
        <Typography2 component="p" variant="titleSmall" css={styles.subtitle}>
          {displaySubtitle}
        </Typography2>
        {quickPrompts.length > 0 && (
          <div css={styles.promptsContainer}>
            {quickPrompts.map((action) => (
              <CoachButton
                key={action.key}
                variant="secondary"
                size="medium"
                onClick={() => handlePromptClick(action.prompt)}
                data-testid={`WESAskCourseraPrompt-${action.key}`}
                css={styles.promptButton}
              >
                {action.prompt}
              </CoachButton>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WESAskCourseraEmptyState;
